import {
    Campaigns,
    CampaignsResponse,
} from '@/api/campaignPlaner/fetchCampaigns';
import { TreeSelect } from '@/components/TreeSelect';
import { CampaignCard } from '@/dashboards/CampaignPlanner/components/CampaignCard';
import { CampaignPlannerTable } from '@/dashboards/CampaignPlanner/components/views/CampaignPlannerTable';
import { CampaignPlannerTimeline } from '@/dashboards/CampaignPlanner/components/views/CampaignPlannerTimeline';
import {
    ExplanationBanner,
    ShowExplanationButton,
    useExplanationBanner,
} from '@/dashboards/CampaignPlanner/components/ExplanationBanner';
import {
    ViewModeSwitch,
    useViewMode,
} from '@/dashboards/CampaignPlanner/components/ViewModeSwitch';
import { useCampaignPlannerContext } from '@/dashboards/CampaignPlanner/hooks/useCampaignPlannerContext';
import { OptimisationBase } from '@/optimizer';
import { useOptimizer } from '@/optimizer/hooks/useOptimizer';
import { extractTimestampFromUuidV7 } from '@analytical-alley/common';
import { Button } from '@analytical-alley/ui';
import { useMemo, useState } from 'react';
import { HiPlus } from 'react-icons/hi';

type FlightStatus = 'past' | 'in_flight' | 'planned';

const FLIGHT_STATUS_OPTIONS: { id: FlightStatus; label: string }[] = [
    { id: 'past', label: 'Past' },
    { id: 'in_flight', label: 'In Flight' },
    { id: 'planned', label: 'Planned' },
];

const OPTIMISATION_BASE_OPTIONS: { id: OptimisationBase; label: string }[] = [
    { id: 'budget', label: 'Media Investment' },
    { id: 'media_kpi', label: 'Media Contribution' },
    { id: 'total_kpi', label: 'Total Contribution' },
];

const getCampaignFlightStatus = (
    campaign: { startDate: Date | string; endDate: Date | string },
    now: Date,
): FlightStatus => {
    if (new Date(campaign.endDate) < now) return 'past';
    if (new Date(campaign.startDate) > now) return 'planned';
    return 'in_flight';
};

type EnrichedCampaign = Campaigns & { createdAt: Date };

interface Props {
    campaigns: CampaignsResponse;
}

export const CampaignPlannerSection = ({ campaigns }: Props) => {
    const { setShowSaveCampaignDialog } = useCampaignPlannerContext();
    const { medias: optimizerMedias } = useOptimizer();
    const {
        isVisible: isExplanationVisible,
        hide: hideExplanation,
        show: showExplanation,
    } = useExplanationBanner();

    const isReady = (optimizerMedias || []).length > 0;

    const onCreateCampaign = () => {
        setShowSaveCampaignDialog(true);
    };

    const enrichedCampaigns: EnrichedCampaign[] = campaigns.map((campaign) => ({
        ...campaign,
        createdAt: extractTimestampFromUuidV7(campaign.id),
    }));

    const [selectedFlightStatuses, setSelectedFlightStatuses] = useState<
        FlightStatus[]
    >(() => FLIGHT_STATUS_OPTIONS.map((option) => option.id));
    const [selectedOptimisationBases, setSelectedOptimisationBases] = useState<
        OptimisationBase[]
    >(() => OPTIMISATION_BASE_OPTIONS.map((option) => option.id));
    const [viewMode, setViewMode] = useViewMode('grid');

    const optimisationBaseNodes = useMemo(
        () =>
            OPTIMISATION_BASE_OPTIONS.map((option) => ({
                id: option.id,
                label: option.label,
                selected: true,
            })),
        [],
    );

    const flightStatusNodes = useMemo(
        () =>
            FLIGHT_STATUS_OPTIONS.map((option) => ({
                id: option.id,
                label: option.label,
                selected: true,
            })),
        [],
    );

    const optimisationBaseLabel =
        selectedOptimisationBases.length === OPTIMISATION_BASE_OPTIONS.length
            ? 'All campaign types'
            : `${selectedOptimisationBases.length} of ${OPTIMISATION_BASE_OPTIONS.length} campaign types`;

    const flightStatusLabel =
        selectedFlightStatuses.length === FLIGHT_STATUS_OPTIONS.length
            ? 'All statuses'
            : `${selectedFlightStatuses.length} of ${FLIGHT_STATUS_OPTIONS.length} statuses`;

    const displayCampaigns = useMemo(() => {
        const now = new Date();
        return enrichedCampaigns.filter((campaign) => {
            if (
                !selectedOptimisationBases.includes(campaign.optimizationBase)
            ) {
                return false;
            }

            const flightStatus = getCampaignFlightStatus(campaign, now);
            return selectedFlightStatuses.includes(flightStatus);
        });
    }, [enrichedCampaigns, selectedFlightStatuses, selectedOptimisationBases]);

    const renderView = () => {
        switch (viewMode) {
            case 'grid':
                return (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                        {displayCampaigns
                            .sort((a, b) =>
                                a.createdAt <= b.createdAt ? 1 : -1,
                            )
                            .map((campaign) => (
                                <CampaignCard
                                    key={campaign.id}
                                    campaign={campaign}
                                />
                            ))}
                    </div>
                );
            case 'table':
                return (
                    <CampaignPlannerTable
                        campaigns={displayCampaigns.sort((a, b) =>
                            a.createdAt <= b.createdAt ? 1 : -1,
                        )}
                    />
                );
            case 'timeline':
                return (
                    <CampaignPlannerTimeline
                        campaigns={displayCampaigns.sort((a, b) =>
                            a.startDate <= b.startDate ? -1 : 1,
                        )}
                    />
                );
        }
    };

    return (
        <div className="space-y-6">
            {isExplanationVisible && (
                <ExplanationBanner
                    length={campaigns.length}
                    onClose={hideExplanation}
                />
            )}

            <div className="flex flex-row justify-between gap-4 flex-wrap-reverse items-center">
                <ViewModeSwitch value={viewMode} onChange={setViewMode} />

                <div className="flex flex-row gap-4 items-center">
                    {!isExplanationVisible && (
                        <ShowExplanationButton onClick={showExplanation} />
                    )}
                    <TreeSelect
                        dataNodes={optimisationBaseNodes}
                        className="py-0"
                        placement="bottom-end"
                        label={optimisationBaseLabel}
                        onCheckedChange={(checkedNodes) => {
                            const nextSelection =
                                OPTIMISATION_BASE_OPTIONS.filter((option) =>
                                    checkedNodes.some(
                                        (node) => node.label === option.label,
                                    ),
                                ).map((option) => option.id);

                            setSelectedOptimisationBases(nextSelection);
                        }}
                    />

                    <TreeSelect
                        dataNodes={flightStatusNodes}
                        className="py-0"
                        placement="bottom-end"
                        label={flightStatusLabel}
                        onCheckedChange={(checkedNodes) => {
                            const nextSelection = FLIGHT_STATUS_OPTIONS.filter(
                                (option) =>
                                    checkedNodes.some(
                                        (node) => node.label === option.label,
                                    ),
                            ).map((option) => option.id);

                            setSelectedFlightStatuses(nextSelection);
                        }}
                    />

                    <Button
                        color="glass"
                        size="md"
                        onClick={onCreateCampaign}
                        disabled={!isReady}
                        tooltip="Set up a new media campaign."
                    >
                        <HiPlus className="mr-2 h-4 w-4" />
                        Create New Campaign
                    </Button>
                </div>
            </div>

            {renderView()}
        </div>
    );
};
